import React from 'react';
import {getGradients} from '../../util/ChartsUtil';
import {Chart} from '@/web-components';
import {chartTokens} from '@forecast-it/design-system';
import {remapAndFormatFinancialMessage} from '../../../../../../forecast-app/shared/util/FinancialInternationalisationUtil';
import CompanySetupUtil from '../../../../../../forecast-app/shared/util/CompanySetupUtil';

const RevenueChart = ({
	project,
	data,
	hasBaseline,
	eyeOptionMap,
	modifyChartOptions,
	showFixedPriceGraphs,
	isUsingNewFixedPriceCalculation,
	isExpenseView,
}) => {
	const formatMessage = remapAndFormatFinancialMessage;
	const isEstimatedInPoints = project.estimationUnit === 'POINTS';
	const {
		billableActualTimeAndExpensesChartData,
		billablePlannedTimeAndExpensesChartData,
		billableTotalTimeAndExpensesAtCompletionChartData,
		baselineTimeAndExpensesChartData,
		rateBasedBillableTotalTimeAndExpensesAtCompletionChartData,
		totalRevenueRecognitionChartData,
	} = data;

	const {gradientFill, gradientStrokeActual, gradientFillActual} = getGradients();
	const dataSets = [];

	const chartColors = chartTokens.chart.color;

	// Baseline Value of Service
	if (hasBaseline && eyeOptionMap.baseline) {
		dataSets.unshift({
			name: formatMessage({
				id: 'baseline.value_of_service',
			}),
			color: chartColors['4'],
			backgroundColor: 'transparent',
			data: baselineTimeAndExpensesChartData,
			borderWidth: 1,
		});
	}

	// Estimated Billable Value of Service
	if (!hasBaseline && !isEstimatedInPoints && eyeOptionMap.plan) {
		dataSets.unshift({
			name: formatMessage({
				id: isExpenseView
					? 'project_budget.planned_billable_expenses'
					: CompanySetupUtil.hasExpenses()
					? 'project_budget.planned_billable_time_and_expenses'
					: 'project_budget.planned_billable_time',
			}),
			color: chartColors['4'],
			filled: true,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : gradientFill,
			data: billablePlannedTimeAndExpensesChartData,
			borderWidth: 1,
		});
	}

	if (billableActualTimeAndExpensesChartData.length && eyeOptionMap.actual) {
		dataSets.unshift({
			name: formatMessage({
				id: isExpenseView
					? 'project_budget.actual_billable_expenses_to_date'
					: CompanySetupUtil.hasExpenses()
					? 'project_budget.actual_billable_time_and_expenses'
					: 'project_budget.actual_billable_time',
			}),
			color: gradientStrokeActual,
			filled: true,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : gradientFillActual,
			data: billableActualTimeAndExpensesChartData,
		});
	}

	// Projected Total Billable Value of Service
	if (
		showFixedPriceGraphs &&
		isUsingNewFixedPriceCalculation &&
		rateBasedBillableTotalTimeAndExpensesAtCompletionChartData.length
	) {
		dataSets.unshift({
			name: formatMessage({id: 'project_budget.rate_based_forecast_revenue'}),
			color: chartColors['3'],
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : '#fff',
			data: rateBasedBillableTotalTimeAndExpensesAtCompletionChartData,
			borderWidth: 3,
		});
	}

	// Projected Total Revenue
	if (showFixedPriceGraphs && isUsingNewFixedPriceCalculation && totalRevenueRecognitionChartData.length) {
		dataSets.unshift({
			name: formatMessage({id: 'project_budget.recognition_amount'}),
			color: chartColors['2'],
			filled: true,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : gradientFillActual,
			data: totalRevenueRecognitionChartData,
		});
	}

	if (billableTotalTimeAndExpensesAtCompletionChartData.length && eyeOptionMap.forecast && !isUsingNewFixedPriceCalculation) {
		dataSets.unshift({
			name: formatMessage({id: 'project_budget.forecast_revenue'}),
			color: gradientStrokeActual,
			filled: true,
			backgroundColor: 'transparent',
			data: billableTotalTimeAndExpensesAtCompletionChartData,
			dashed: true,
		});
	}

	return <Chart dataSets={dataSets} modifyOptions={modifyChartOptions} usePointStyle />;
};

export default RevenueChart;
